<script setup>
import {
  useHead,
  useRoute,
  useScrollTopOnMounted,
  useSanityQuery,
  computed,
  watchEffect,
  ref,
  useImageSource,
  onMounted,
} from '#imports';
import { getCommonMetaTags } from '~/utils/get-common-meta-tags';
import { getQuery as getPrideQuery } from '~/groq-queries/pride';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay } from 'swiper/modules';

const route = useRoute();
const showCarousel = ref(false);

watchEffect(() => {
  showCarousel.value = route.path === '/pride';
});

const modules = [Autoplay];

useScrollTopOnMounted();

const title = 'DOXA Прайд';
const description =
  'В июне мы отмечаем месяц гордости, посвященный свободе быть собой для всех представителей ЛГБТКИА+ сообщества. Присоединяйтесь к нашему онлайн-прайду и высказывайтесь на DOXA';

const contentQuery = getPrideQuery();
const { data: content } = await useSanityQuery(contentQuery);

const ogImageUrl = computed(() =>
  useImageSource({
    image: content.value.ogImage,
  })
);

useHead({
  title,
  meta: getCommonMetaTags({
    path: route.fullPath,
    ogType: 'website',
    title,
    description,
    ogImageUrl: ogImageUrl.value,
  }),
});

const allCards = computed(() => content.value.prideSpecialCards);
const swiper = ref();
function setSwiper(swiperInstance) {
  swiper.value = swiperInstance;
}

const dataLoaded = ref(false); // Создаем реактивное свойство

onMounted(async () => {
  await useSanityQuery(contentQuery); // Загружаем данные
  dataLoaded.value = true; // После загрузки данных изменяем значение на true
});
</script>

<template>
  <LayoutStaticPage is-pride is-transparent-header>
    <div class="pride-page">
      <div class="antiwarletter-page__cover">
        <PageCover
          is-pride-special
          :image-src="content.image"
          :title-image="content.titleImage"
          :title-image-mobile="content.titleImageMobile"
        />
      </div>
      <main class="pride-page__container">
        <div class="post-content">
          <p>
            Недавно здесь DOXA провела прайд. Мы благодарны всем принявшим
            участие.
          </p>
        </div>

        <div v-if="showCarousel" class="swiper-container">
          <Swiper
            v-if="dataLoaded"
            :space-between="60"
            :auto-height="true"
            :loop="true"
            :autoplay="true"
            :modules="modules"
            :grab-cursor="true"
            @swiper="setSwiper"
          >
            <SwiperSlide
              v-for="(card, index) in allCards.slice(-3)"
              :key="`message-${index}`"
              class="pride-page__slide"
            >
              <div v-if="card.image" class="pride-card__avatar">
                <BaseAvatar :image="card.image" size="large" />
              </div>
              <div class="pride-page__card">
                <div class="pride-card__header">
                  <h3>{{ card.name }}</h3>
                </div>
                <div class="pride-card__content">
                  <p>{{ card.text }}</p>
                </div>
              </div>
            </SwiperSlide>
          </Swiper>
        </div>

        <div class="post-content">
          <p>
            Вы — удивительные! Ваши слова останутся на сайте навсегда, увидимся
            в следующем году!
          </p>
        </div>

        <div class="pride-page__button-wrapper">
          <a href="https://ei3mgr4e-bsccljbcrq-ez.a.run.app/pride-feed" target="_blank">
            <ButtonComponent
              button-caption="Прошедший прайд"
              theme="green"
              size="large"
            ></ButtonComponent>
          </a>
        </div>
      </main>
    </div>
  </LayoutStaticPage>
</template>

<style lang="postcss" scoped>
.pride-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  .post-content > p {
    font-size: var(--body-font-size);
    line-height: var(--body-line-height);
    font-weight: 400;
    position: relative;
    margin: 16px 0;
  }

  @media (--small-vp) {
    p {
      font-size: 16px;
      line-height: 22px;
      margin: 8px 0;
    }
  }

  .post-content {
    max-width: var(--content-max-width);
    margin-right: auto;
    margin-left: auto;
    padding: var(--article-content-padding-medium-vp);

    @media (--medium-vp) {
      padding: var(--article-content-padding-medium-vp);
    }

    @media (--tablet-vp) {
      padding: var(--tablet-layout-padding);
    }

    @media (--small-vp) {
      padding: 0 12px;
    }
  }

  main {
    flex-grow: 1;
    max-width: 100%;
  }

  &__button-wrapper {
    display: flex;
    justify-content: center;
    margin: 36px 0;
  }

  a {
    display: block;
    max-width: 350px;
    flex-shrink: 0;
    flex-grow: 1;
  }

  a .button--large {
    margin: 0 auto;
  }

  .swiper {
    z-index: 0;
    max-width: 600px;
    margin: 0 auto;
    padding: 20px;
  }

  .swiper .swiper-wrapper {
    transition-timing-function: linear;
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .swiper-container {
    margin: 25px 0;
    min-height: 230px;
    max-height: 230px;
    display: flex;
    align-items: center;

    @media (--x-small-vp) {
      min-height: 340px;
    }
  }

  &__slide {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: center;
    gap: 20px;
  }

  .pride-page__card {
    background-color: #f9f9f9;
    border: 1px solid #000;
    box-shadow: 0 0 0 -1px rgba(0, 0, 0, 100%),
      5px 5px 0 0 rgba(116, 255, 171, 100%);
    padding: 24px;
    height: fit-content;
    position: relative;
    min-width: 232px;

    @media (--x-small-vp) {
      min-width: 180px;
    }
  }

  .pride-card__content {
    margin-top: 16px;
  }

  .pride-card__avatar {
    width: 90px;
    height: 90px;
    border-radius: 50%;
    box-shadow: 0 0 0 -1px rgba(0, 0, 0, 100%),
      5px 5px 0 0 rgba(116, 255, 171, 100%);
  }

  .pride-card__header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  &__container {
    max-width: var(--content-narrow-max-width);
    margin: 24px auto 120px auto;
    padding: 0;

    @media (--tablet-vp) {
      padding: 0 12px;
    }
  }
}
</style>
